import { computed, ref, watch } from 'vue'
import { partidaService } from '&/services/partidaService.js'
import { gameService } from '&/services/gameService.js'

// Estados globales con refs individuales
const game = ref(null);

const getGameById = async (gameId) => {
  try {
    const response = await gameService.getGameById(gameId);
    if (response.success && response.data) {
      game.value = response.data;
      return response;
    }
    return response;
  } catch (error) {
    console.error('❌ Error fetching game by ID:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Composable específico para acceso al juego
 */
export function useGame() {
  return {
    getGameById,
    game
  }
}

const partida = ref(null)

watch(partida, async (newPartida) => {
  if (newPartida) {
    try {
      const gameData = await gameService.getGameById(newPartida.gameId);
      if (!gameData.success) {
        throw new Error('Error fetching game data');
      } else {
        game.value = gameData.data;
      }

      if (newPartida.gameState && newPartida.gameState.currentSceneId) {
        const currentSceneId = newPartida.gameState.currentSceneId;
        const currentScene = game.value.scenes.find(scene => scene.id === currentSceneId);

        scene.value = currentScene;

        if (currentScene.chat) {
          chat.value = currentScene.chat;
          puzzle.value = null;
        } else if (currentScene.puzzle) {
          chat.value = null;
          puzzle.value = game.value.is_reduced && currentScene.puzzle_reduced ? currentScene.puzzle_reduced : currentScene.puzzle;
        }
      } else {
        scene.value = null;
        chat.value = null;
        puzzle.value = null;
      }
    } catch (error) {
      console.error('Error fetching partida state:', error);

      game.value = null;
      partida.value = null;
      scene.value = null;
      puzzle.value = null;
      chat.value = null;
    }
  }
}, { deep: true })

const loadSessionPartida = async () => {
  try {
    const response = await partidaService.getSessionPartida()
    if (response.success && response.data) {
      partida.value = response.data
      return response
    }
    return response
  } catch (error) {
    console.error('❌ Error cargando partida de sesión:', error)
    return { success: false, error: error.message }
  }
}

const createNewPartida = async (gameId, gameCode) => {
  try {
    const response = await partidaService.createPartida(gameId, gameCode)
    if (response.success && response.data) {
      partida.value = response.data
      return response
    }
    return response
  } catch (error) {
    console.error('❌ Error creando nueva partida:', error)
    return { success: false, error: error.message }
  }
}

const getPartida = async (partidaCode) => {
  try {
    const response = await partidaService.getPartida(partidaCode)
    if (response.success && response.data) {
      partida.value = response.data
      return response
    }
    return response
  } catch (error) {
    console.error('❌ Error cargando partida:', error)
    return { success: false, error: error.message }
  }
}

const updatePartidaState = async (partidaCode, currentSceneId, currentPuzzleState, puzzleStartedAt) => {
  try {
    const response = await partidaService.updatePartidaState(partidaCode, currentSceneId, currentPuzzleState, puzzleStartedAt)
    if (response.success && response.data) {
      // Actualizar el estado local con la respuesta del servidor
      partida.value = response.data;
      return response
    }
    return response
  } catch (error) {
    console.error('❌ Error actualizando estado de partida:', error)
    return { success: false, error: error.message }
  }
}

const updatePartidaStatus = async (partidaCode, status) => {
  try {
    const response = await partidaService.updatePartidaStatus(partidaCode, status)
    if (response.success && response.data) {
      // Actualizar el estado local con la respuesta del servidor
      partida.value = response.data;
      return response
    }
    return response
  } catch (error) {
    console.error('❌ Error actualizando status de partida:', error)
    return { success: false, error: error.message }
  }
}

const setPlayersCount = async (partidaCode, playersCount) => {
  try {
    const response = await partidaService.setPlayersCount(partidaCode, playersCount)
    if (response.success && response.data) {
      partida.value = response.data
      return response
    }
    return response
  } catch (error) {
    console.error('❌ Error estableciendo número de jugadores:', error)
    return { success: false, error: error.message }
  }
}

const updatePartidaSessionId = async (partidaCode, sessionId) => {
  try {
    const response = await partidaService.updatePartidaSessionId(partidaCode, sessionId)
    if (response.success && response.data) {
      partida.value = response.data
      return response
    }
    return response
  } catch (error) {
    console.error('❌ Error actualizando ID de sesión de partida:', error)
    return { success: false, error: error.message }
  }
}

const setPuzzleStartedAt = async (partidaCode, puzzleStartedAt = null) => {
  try {
    const response = await partidaService.setPuzzleStartedAt(partidaCode, puzzleStartedAt)
    if (response.success && response.data) {
      partida.value = response.data
      return response
    }
    return response
  } catch (error) {
    console.error('❌ Error estableciendo timestamp de puzzle:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Composable específico para acceso a la partida
 */
export function usePartida() {
  return {
    partida,
    loadSessionPartida,
    createNewPartida,
    getPartida,
    updatePartidaState,
    updatePartidaStatus,
    setPlayersCount,
    updatePartidaSessionId,
    setPuzzleStartedAt
  }
}

const scene = ref(null)

/**
 * Composable específico para acceso a la scene actual
 */
export function useScene() {
  return {
    scene
  }
}

const puzzle = ref(null)

/**
 * Composable específico para acceso al puzzle actual
*/
export function usePuzzle() {
  return {
    puzzle
  }
}

const chat = ref(null)

const availableCharacters = computed(() => {
  if (game.value && chat.value) {
    return game.value.chat_characters || []
  }
})

/**
 * Composable específico para acceso al chat
 */
export function useChat() {
  return {
    chat,
    availableCharacters
  }
}
